import { useEffect } from 'react';

import { useSelector } from 'react-redux';

import {
  BRAND_DATA,
  BRAND_IDENTITIES,
  BRANDING_LOCAL_STORAGE_KEY,
  BRANDING_QUERY_PARAM,
} from './useBranding.constants';
import {
  GLAMMATIC_MAIL_TO_SUPPORT_EMAIL,
  GLAMMATIC_SUPPORT_EMAIL,
  MAIL_TO_SUPPORT_EMAIL,
  SUPPORT_EMAIL,
} from '../../../../../constants/constants';
import useQueryParams from '../../../../../hooks/useQueryParams';
import storage from '../../../../../libs/storage';
import { selectBusiness } from '../../../../../selectors';

/*
  useBranding

  Defines the appropriate logo and brand text, based on the following rules:

  1. Always use the branding defined for the website in the database. This is
     usually set during the onboarding process.
  2. If on a login page or non-website page, check whether a query param was
     given to define the branding. If so, use the cached value.
  3. If no cached value, default to the Fisherman logo and brand text.
*/
function useBranding() {
  const localStorageValue = storage.get(BRANDING_LOCAL_STORAGE_KEY);
  const business = useSelector(selectBusiness);
  const brandingQueryParam = useQueryParams(BRANDING_QUERY_PARAM).get();

  function setBrandingFromQueryParam() {
    storage.set(brandingQueryParam, BRANDING_LOCAL_STORAGE_KEY);
  }

  function getBrand(brand) {
    if (brand === BRAND_IDENTITIES.GLAMMATIC) {
      return BRAND_DATA[BRAND_IDENTITIES.GLAMMATIC];
    }
    return BRAND_DATA[BRAND_IDENTITIES.FISHERMAN];
  }

  function getSupportEmail(brandIdentity) {
    return brandIdentity === BRAND_IDENTITIES.GLAMMATIC ? GLAMMATIC_SUPPORT_EMAIL : SUPPORT_EMAIL;
  }

  function getMailToSupportEmail(brandIdentity) {
    return brandIdentity === BRAND_IDENTITIES.GLAMMATIC
      ? GLAMMATIC_MAIL_TO_SUPPORT_EMAIL
      : MAIL_TO_SUPPORT_EMAIL;
  }

  useEffect(() => {
    if (brandingQueryParam) setBrandingFromQueryParam();
  }, []);

  let brand;
  // by default: use the branding that is specified for the website.
  if (business?.brandIdentity) {
    brand = getBrand(business?.brandIdentity);
  } else if (localStorageValue === BRAND_IDENTITIES.GLAMMATIC) {
    // on the login page: check the brand identity that's cached to localStorage
    brand = getBrand(BRAND_IDENTITIES.GLAMMATIC);
  } else {
    // always default to Fisherman branding when unknown
    brand = getBrand(BRAND_IDENTITIES.FISHERMAN);
  }
  const { brandLogoURL, brandName, menuLabel } = brand;

  // Add brand identity checks for easy use
  const isGlammatic = brandName === BRAND_DATA[BRAND_IDENTITIES.GLAMMATIC].brandName;
  const isFisherman = brandName === BRAND_DATA[BRAND_IDENTITIES.FISHERMAN].brandName;

  return {
    brandName,
    brandLogoURL,
    menuLabel,
    isGlammatic,
    isFisherman,
    getSupportEmail,
    getMailToSupportEmail,
  };
}

export default useBranding;
